import { Vector3, type WebGLRenderer } from "three";
import { gameColor, setWorldAccent } from "../render/style/roles.ts";
import { setRingLight } from "../render/atmosphere.ts";
import { GpuTimer } from "../engine/gpu-timer.ts";
import { AVATAR_LOOKS, CLASS_IDS, classColor, classOf, DETAIL_PIECE_IDS, DETAIL_PIECES, GROUND_DETAIL_BY_ID, MELEE, NATURAL_CHESTS, RESOURCE_TYPES, SIMPATIA_CHESTS, type ClassId, type GroundDetailId } from "@eldrun/content";
import { BIOME_NAMES, CHUNK_SIZE, CHUNKS_PER_SIDE, formatSeed, GEN_VERSION, poiCenters, poiChests, poiColliders, poiCollidersHash, poiMascates, poiSimpatiaChests, sampleHeight, WORLD_HALF } from "@eldrun/worldgen";
import { poiLook } from "../game/poi-layer.ts";
import { planBiomes } from "../game/resource-layer.ts";
import { StructureLayer } from "../game/structure-layer.ts";
import { clampDelta, FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { FreeCameraController, type FreeCameraState } from "../input/free-camera.ts";
import { AVATAR_CLIPS } from "../assets/avatars.generated.ts";
import { createAvatarView } from "../game/avatar-view.ts";
import { attackEnergy, SkinnedAvatar } from "../game/skinned-avatar.ts";
import { heldMesh } from "../game/held-item.ts";
import { createCreatureModel } from "../game/creature-model.ts";
import { Ribbon, RIBBON_SAMPLES, ribbonLook, swingEnds, swingTip } from "../game/ribbons.ts";
import { createSlotRing } from "../render/shaders/slot-ring.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import type { AvatarLibrary } from "../render/avatar-library.ts";
import { applyToonStyle, MAX_BLOBS, TOON_STYLE, type ToonStyle } from "../render/atmosphere.ts";
import { BlobShadows, PLAYER_BLOB_RADIUS } from "../render/blob-shadows.ts";
import { QUALITY } from "../render/quality.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { probeAt, shadingProbe, type ShadingProbeOptions, type ShadingProbeResult } from "../render/shading-probe.ts";
import { WorldView } from "../render/world-view.ts";
import { DETAIL_GROUPS, PIECE_COUNT } from "../world/detail-scatter.ts";
import type { LoadedWorld } from "../world/load-world.ts";

/** Captura de referência no terreno real: mesma composição para as quatro classes e os dois estados. */
export interface IslandReference {
  classId: ClassId;
  clip: "parado" | "golpe";
  time?: number;
  /** Câmera do quadro (m, rad); herói e alvo saem dela, à frente-direita e ao fundo-esquerda. */
  frame?: { x: number; z: number; yaw: number };
  /** O herói a `ahead` m à frente e `side` m à direita da câmera (padrão: o quadro "Mintlit", 4,6 e 1,9). */
  hero?: { ahead: number; side: number };
  /** O alvo (e para onde o herói olha) a `ahead` m à frente e `side` m à direita da câmera (padrão 19 e −2,6; Fase 14). */
  target?: { ahead: number; side: number };
}

/**
 * O quadro padrão (semente 0000C1S; Fase 13, a referência "Mintlit"): campo aberto com capim, copas
 * espalhadas até o horizonte e o entardecer ao fundo.
 */
const REFERENCE_FRAME = { x: -100, z: -560, yaw: 1.55 } as const;

/** Um ponto no chão a `ahead` m à frente e `side` m à direita da câmera do quadro. */
function framePoint(frame: { x: number; z: number; yaw: number }, ahead: number, side: number): { x: number; z: number } {
  return { x: frame.x - Math.sin(frame.yaw) * ahead + Math.cos(frame.yaw) * side, z: frame.z - Math.cos(frame.yaw) * ahead - Math.sin(frame.yaw) * side };
}

const REFERENCE_WEAPONS: Readonly<Record<ClassId, string>> = { baluarte: "espada-prata", lanceiro: "lanca-prata", arcanista: "cajado-prata", patrulheiro: "arco-prata" };

export interface IslandViewer {
  /** Todos os chunks já têm malha. */
  readonly complete: boolean;
  /** Fase 13: GPU mediana (ms) do desenho em `frames` quadros (`undefined` sem o medidor de GPU). */
  gpuProfile(frames: number): Promise<number | undefined>;
  stats(): Record<string, unknown>;
  /** Sonda de sombreamento (F51): a vista atual no dia, no crepúsculo e na noite (F52: e pelo pós de um tier). */
  shading(options?: ShadingProbeOptions): ShadingProbeResult;
  /**
   * Chão ao lado de cada boneco (`&bonecos=`), para longe da luz (a 0,5, 0,9, 1,3 e 1,7 raio da sombra)
   * e de través (a 0,6 e 0,85 raio, dos dois lados), com as sombras-blob e sem (F51); `grass: false`
   * esconde o capim (só o terreno).
   */
  blobTell(options?: { grass?: boolean }): { samples: { on: number[] | null; off: number[] | null }[] }[];
  /**
   * Só o capim (F51): acha um lugar com capim perto da câmera, põe ali uma sombra-blob de teste (1 m
   * de raio) e devolve a cor média das lâminas vistas de cima num quadrado de 2 m, sem neblina, com a
   * sombra e sem (a sombra é calculada no vértice das lâminas).
   */
  grassTell(): { x: number; z: number; covered: number; on: number[]; off: number[] } | undefined;
  stop(): void;
}

/**
 * Visualizador da ilha com câmera livre (`?debug=ilha`), com os pontos de interesse (adereços e
 * estruturas do mundo). `focus` (`?poi=acampamento`) começa a câmera olhando o primeiro POI do tipo
 * e, com `look` (`&montagem=1`), o primeiro com aquela montagem (F43); `resource` (`?recurso=rocha`),
 * a primeira instância daquele recurso, de perto (conferir modelos), opcionalmente num bioma
 * (`&bioma=cerrado`, para conferir as paletas). `details.focus` (`&detalhe=tabua`) enquadra um enfeite
 * do chão (F44), também no bioma pedido, e `details.off` (`&detalhes=0`) os desliga (comparação A/B).
 * `frozen` (`&tempo=0`, F51) para o relógio dos shaders e das estruturas em 0: vento, ondas e
 * chamas ficam iguais a cada quadro (o golden da sonda de sombreamento); `style` (`&estilo=`), o
 * estilo do toon (`&estilo=0`: sem os termos da F51, para comparar); `dolls` (`&bonecos=4`), jogadores
 * parados em volta do POI enquadrado, para ver as sombras-blob no terreno e no capim: os avatares
 * animados (F49, um personagem por cor, no `parado`) com `avatarLibrary`, ou os bonecos procedurais.
 */
export function startIslandViewer(
  renderer: WebGLRenderer,
  world: LoadedWorld,
  initialPhase = 0.15,
  focus?: string,
  resource?: string,
  biome?: string,
  look?: number,
  details: { focus?: string | undefined; off?: boolean } = {},
  frozen = false,
  style: ToonStyle = TOON_STYLE,
  dolls = 0,
  avatarLibrary?: AvatarLibrary,
  reference?: IslandReference,
  modelLibrary?: ModelLibrary,
): IslandViewer {
  // Sem as Opções (só o jogo as lê): os enfeites ficam na densidade cheia, ou desligados para comparar.
  QUALITY.details = details.off ? 0 : 1;
  const view = new WorldView(renderer, world, initialPhase);
  applyToonStyle(view.atmosphere, style);
  const height = (x: number, z: number) => sampleHeight(world.heightfield, x, z);
  const structures = new StructureLayer(view.scene, view.foliage, undefined, { treasure: view.treasure });
  let n = 0;
  const place = (itemId: string, x: number, z: number, yaw = 0) => structures.add(`poi${n++}`, { itemId, x, y: height(x, z), z, yaw });
  for (const c of poiChests(world.plan.pois)) place(NATURAL_CHESTS[c.kind].item, c.x, c.z);
  for (const c of poiSimpatiaChests(world.plan.pois)) place(SIMPATIA_CHESTS[c.kind].item, c.x, c.z);
  for (const c of poiCenters(world.plan.pois)) place(c.item, c.x, c.z, c.rot);
  for (const m of poiMascates(world.plan.pois)) place("mascate", m.x, m.z, Math.atan2(m.x - m.faceX, m.z - m.faceZ));
  const target = focus ? world.plan.pois.find((p) => p.type === focus && (look === undefined || poiLook(p, world.seed) === look)) : undefined;
  // Colisores do POI enquadrado (os mesmos de `poiColliders` no servidor e na predição): contagens e a
  // faixa de altura dos tetos suspensos (a laje da Caverna) acima do chão do centro.
  const focusColliders = target && (() => {
    const c = poiColliders([target], height);
    const ground = height(target.x, target.z);
    const ceilings = c.boxes.filter((b) => b.ceiling);
    return {
      cylinders: c.cylinders.length,
      boxes: c.boxes.length,
      ceilings: ceilings.length,
      ceilingRange: ceilings.length ? [Math.min(...ceilings.map((b) => b.minY)) - ground, Math.max(...ceilings.map((b) => b.maxY)) - ground] : undefined,
    };
  })();
  // Todos os colisores dos POIs como o servidor e a predição os calculam: contagens e a soma (o e2e
  // confere com o golden do Node).
  const allColliders = (() => {
    const c = poiColliders(world.plan.pois, height);
    return { cylinders: c.cylinders.length, boxes: c.boxes.length, ceilings: c.boxes.filter((b) => b.ceiling).length, hash: poiCollidersHash(c) };
  })();
  const near = resource ? resourceFocus(world, resource, biome) : undefined;
  const shot = reference?.frame ?? REFERENCE_FRAME;
  const heroAt = framePoint(shot, reference?.hero?.ahead ?? 4.6, reference?.hero?.side ?? 1.9);
  const targetAt = framePoint(shot, reference?.target?.ahead ?? 19, reference?.target?.side ?? -2.6);
  const start = reference ? { x: shot.x, y: Math.max(height(shot.x, shot.z) + 2.4, height(heroAt.x, heroAt.z) + 2.7), z: shot.z, yaw: shot.yaw, pitch: -0.06 } : near ?? (target
    ? (() => {
        const x = target.x + Math.sin(target.rot) * 24;
        const z = target.z + Math.cos(target.rot) * 24;
        return { x, y: height(target.x, target.z) + 11, z, yaw: Math.atan2(x - target.x, z - target.z), pitch: -0.38 };
      })()
    : { x: 0, y: world.plan.stats.maxHeight + 120, z: WORLD_HALF * 0.75, yaw: 0, pitch: -0.32 });
  const controller = new FreeCameraController(renderer.domElement, start);
  if (reference) {
    view.camera.fov = 60;
    view.camera.updateProjectionMatrix();
  }
  // O enquadramento de referência conserva solo/colliders: só câmera e atores de depuração são postos.
  // A pose é amostrada do modelo real; não há boneco substituto nesta comparação visual.
  const referenceHero = reference && avatarLibrary?.has(classOf(reference.classId).character)
    ? new SkinnedAvatar(avatarLibrary, view.atmosphere, classOf(reference.classId).character, classColor(reference.classId), 0)
    : undefined;
  const referenceTarget = reference ? createCreatureModel(view.atmosphere, "corpo-seco") : undefined;
  const referenceWeaponId = reference ? REFERENCE_WEAPONS[reference.classId] : undefined;
  const referenceWeapon = referenceHero && referenceWeaponId && modelLibrary
    ? heldMesh(referenceWeaponId, modelLibrary, view.heldFor(classColor(reference!.classId)), referenceHero.heldView)
    : undefined;
  const referenceRibbon = referenceHero && referenceWeapon && reference && (reference.classId === "baluarte" || reference.classId === "lanceiro") ? new Ribbon() : undefined;
  if (referenceHero && reference) {
    referenceHero.root.position.set(heroAt.x, height(heroAt.x, heroAt.z), heroAt.z);
    referenceHero.root.rotation.y = Math.atan2(heroAt.x - targetAt.x, heroAt.z - targetAt.z);
    referenceHero.setHeld(referenceWeapon, referenceWeaponId);
    referenceHero.root.add(createSlotRing(view.atmosphere, classColor(reference.classId), 1));
    // O mundo na cor da classe do herói do quadro (Fase 13, ADR 0026), e o anel acendendo o chão.
    setWorldAccent(classColor(reference.classId));
    setRingLight(view.atmosphere, referenceHero.root.position.x, referenceHero.root.position.y, referenceHero.root.position.z, gameColor(classColor(reference.classId)));
    view.scene.add(referenceHero.root);
    if (referenceRibbon) view.scene.add(referenceRibbon.mesh);
  }
  if (referenceTarget) {
    referenceTarget.root.position.set(targetAt.x, height(targetAt.x, targetAt.z), targetAt.z);
    referenceTarget.root.rotation.y = Math.PI;
    view.scene.add(referenceTarget.root);
  }
  const ribbonBase = new Vector3();
  const ribbonTip = new Vector3();
  let referenceTime = reference?.time ?? 0;
  const poseReference = (time: number) => {
    if (!referenceHero || !reference) return;
    const duration = AVATAR_CLIPS[reference.clip].duration;
    const clipTime = time % duration;
    if (reference.clip === "golpe" && referenceRibbon && referenceWeapon) {
      const look = ribbonLook("golpe", classColor(reference.classId), 1, QUALITY.post.reduceFlashes);
      referenceRibbon.begin(look);
      const from = Math.max(0, clipTime - look.lifeMs / 1000);
      for (let i = 0; i <= RIBBON_SAMPLES; i++) {
        const t = from + (clipTime - from) * i / RIBBON_SAMPLES;
        referenceHero.showClip("golpe", t);
        referenceWeapon.updateWorldMatrix(true, false);
        swingEnds(referenceWeapon, ribbonBase, ribbonTip);
        referenceRibbon.sample(t * 1000, ribbonBase.x, ribbonBase.y, ribbonBase.z, ribbonTip.x, ribbonTip.y, ribbonTip.z);
      }
      referenceRibbon.update(clipTime * 1000);
    } else referenceRibbon?.stop();
    referenceHero.showClip(reference.clip, clipTime);
    // Inversa de swingClipTime: o impacto real do clipe corresponde à fração do golpe no servidor.
    // Dividir apenas pela duração acenderia o pico depois do contato da animação.
    const impact = AVATAR_CLIPS.golpe.impact;
    const progress = clipTime <= impact
      ? clipTime / impact * MELEE.impactFraction
      : MELEE.impactFraction + (clipTime - impact) / (AVATAR_CLIPS.golpe.duration - impact) * (1 - MELEE.impactFraction);
    referenceHero.setEnergy(reference.clip === "golpe" ? attackEnergy(progress) : 0);
  };
  // Bonecos em volta do POI (a câmera fica a 24 m dele, olhando para ele), cada um com a sombra-blob.
  const blobs = new BlobShadows(view.atmosphere);
  const avatars = target && !reference
    ? Array.from({ length: Math.min(dolls, 8) }, (_, i) => {
        const doll = createAvatarView(avatarLibrary, view.atmosphere, classColor(CLASS_IDS[i % CLASS_IDS.length]!), AVATAR_LOOKS[i % AVATAR_LOOKS.length]!.character, i);
        const a = target.rot + (i / Math.max(1, dolls)) * Math.PI * 2;
        const x = target.x + Math.sin(target.rot) * 12 + Math.cos(a) * 3;
        const z = target.z + Math.cos(target.rot) * 12 + Math.sin(a) * 3;
        doll.root.position.set(x, height(x, z), z);
        doll.root.rotation.y = a;
        view.scene.add(doll.root);
        return doll;
      })
    : [];
  const people = avatars.map((doll) => doll.root.position);
  // Enquadrar um enfeite pede os chunks ao worker: a câmera pula para ele quando achar.
  let detailTarget: { x: number; z: number; biome: string } | undefined;
  let searching = !reference && details.focus !== undefined;
  if (!reference && details.focus !== undefined) {
    void detailFocus(world, details.focus, biome)
      .then((found) => {
        if (!found) return;
        controller.state = found.pose;
        detailTarget = found.target;
      })
      .catch((error: unknown) => console.warn("enfeite não encontrado", error))
      .finally(() => (searching = false));
  }
  const hud = new StatsHud(document.body);
  const timer = new FrameTimer();
  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const frame = new FrameStats(renderer.info);
  const unwatch = frame.watch(view.scene);
  const post = new PostFx(renderer);
  post.bind(view.atmosphere);
  let phase = initialPhase;
  let last = performance.now();
  let elapsed = 0;
  const seedCode = formatSeed(world.seed);

  // Medida de GPU por quadro (Fase 13, só sob pedido): cada efeito do pós medido à parte.
  const gpu = new GpuTimer(renderer.getContext() as WebGL2RenderingContext);
  let profile: { frames: number; samples: number[]; resolve: (ms: number | undefined) => void } | undefined;
  renderer.setAnimationLoop((now: number) => {
    frame.begin();
    const dt = clampDelta((now - last) / 1000);
    timer.push(now - last);
    last = now;
    elapsed += dt;
    if (controller.isDown("BracketRight")) phase += dt * 0.05;
    if (controller.isDown("BracketLeft")) phase -= dt * 0.05;
    const time = reference ? reference.time ?? (frozen ? 0 : elapsed) : frozen ? 0 : now / 1000;
    referenceTime = time;
    view.setPhase(phase, time);
    // Os bonecos animados respiram no `parado` (no instante 0 com o relógio parado).
    for (const doll of avatars) if (doll instanceof SkinnedAvatar) doll.showClip("parado", time % AVATAR_CLIPS.parado.duration);
    controller.update(dt, view.camera);
    poseReference(time);
    // Baús, mercadores e estruturas do centro: o lote (F36) só desenha o que passa no corte daqui.
    structures.update(time, view.camera.position.x, view.camera.position.z);
    blobs.begin();
    people.forEach((p, i) => blobs.add(`boneco${i}`, p.x, p.y, p.z, PLAYER_BLOB_RADIUS));
    if (referenceHero) {
      const p = referenceHero.root.position;
      blobs.add("referencia-heroi", p.x, p.y, p.z, PLAYER_BLOB_RADIUS);
    }
    if (referenceTarget) {
      const p = referenceTarget.root.position;
      blobs.add("referencia-alvo", p.x, p.y, p.z, PLAYER_BLOB_RADIUS);
    }
    // A captura de referência usa o mesmo contato dos recursos da partida. Os goldens antigos
    // continuam com a sua população original de sombras quando não há `referencia`.
    if (reference) view.resources.castBlobs(blobs, view.camera.position.x, view.camera.position.z);
    blobs.end(view.camera.position.x, view.camera.position.z, MAX_BLOBS);
    if (profile) gpu.begin();
    view.render(post, dt, frame);
    if (profile) {
      gpu.end();
      gpu.poll((ms, current) => {
        if (current && profile) profile.samples.push(ms);
      });
      if (profile.samples.length >= profile.frames) {
        const done = profile;
        profile = undefined;
        // Mediana: um quadro preso (compilação, streaming) não puxa a medida.
        const sorted = [...done.samples].sort((a, b) => a - b);
        done.resolve(sorted[Math.floor(sorted.length / 2)]);
      }
    }
    const f = frame.end();
    hud.update(now, () => {
      const s = view.terrain.stats;
      const p = view.camera.position;
      return [
        `Eldrun · seed ${seedCode} · ${world.mode} · gen v${GEN_VERSION} · hash ${world.worldHash}`,
        `${timer.fps.toFixed(0)} fps · ${timer.averageMs.toFixed(1)} ms (pior ${timer.worstMs.toFixed(1)})`,
        `draw calls ${f.drawCalls} (+${f.postCalls} do pós ${post.plan().tier}) · triângulos ${(f.triangles / 1000).toFixed(0)}k`,
        `chunks L0..L3 ${s.chunksByLod.join("/")} · pendentes ${s.pending} · fila ${world.pool.stats.queued}`,
        `enfeites ${view.details.drawn} em ${view.details.drawCalls} malhas · ${(view.details.triangles / 1000).toFixed(0)}k triângulos`,
        `câmera ${p.x.toFixed(0)}, ${p.y.toFixed(0)}, ${p.z.toFixed(0)} · fase ${(((phase % 1) + 1) % 1).toFixed(2)}`,
        `clique: mouse · WASD/Espaço/C · Shift rápido · [ ] hora do dia`,
        ...(view.terrain.failure ? [`ERRO no terreno: ${view.terrain.failure.message}`] : []),
      ];
    });
  });

  return {
    gpuProfile(frames: number) {
      if (!gpu.available) return Promise.resolve(undefined);
      gpu.mark();
      return new Promise((resolve) => (profile = { frames, samples: [], resolve }));
    },
    get complete() {
      return view.terrain.complete && view.details.complete && !searching;
    },
    stats: () => ({
      // Captura de referência (Gate B3): onde caem na tela (0..1) o anel do herói (na frente e dos lados),
      // a arma (do meio à ponta) e o corpo (peito e cabeça), para medir a cor de cada região.
      ...(referenceHero && {
        referenceProbe: (() => {
          const screen = (v: Vector3) => {
            v.project(view.camera);
            return [(v.x + 1) / 2, (1 - v.y) / 2] as const;
          };
          const p = referenceHero.root.position;
          // Frente (para a câmera) e os dois lados; atrás o corpo esconde o anel.
          const ring = [[-0.62, 0], [0, -0.62], [0, 0.62]].map(([ahead, side]) => screen(new Vector3(p.x - Math.sin(shot.yaw) * ahead! + Math.cos(shot.yaw) * side!, p.y + 0.03, p.z - Math.cos(shot.yaw) * ahead! - Math.sin(shot.yaw) * side!)));
          const top = referenceWeapon ? swingTip(referenceWeapon.geometry) : 0;
          if (referenceWeapon) referenceWeapon.updateWorldMatrix(true, false);
          // A arma fora da faixa do núcleo da fita (o branco somado do núcleo cobre a ponta, de propósito).
          // Ao longo da haste inteira (Fase 13): no preparo da Arcanista, o chapéu de vidro cobre o meio dela; o
          // teste de cor fica com o ponto mais aceso.
          const weapon = referenceWeapon ? [0.1, 0.25, 0.4, 0.55, 0.7, 0.85].map((f) => screen(referenceWeapon.localToWorld(new Vector3(0, top * f, 0)))) : [];
          const body = [0.55, 0.8, 1.05].map((h) => screen(new Vector3(p.x, p.y + h, p.z)));
          return { ring, weapon, body };
        })(),
      }),
      ...view.terrain.stats,
      drawCalls: frame.last.drawCalls,
        shadowCalls: view.shadowCalls,
      triangles: frame.last.triangles,
      frameCalls: frame.last.frameCalls,
      // Programas da cena (F52: os do pós à parte, em `post.programs`).
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(frame.last),
      reference: reference && {
        classId: reference.classId, color: classColor(reference.classId), clip: reference.clip, time: referenceTime,
        hero: referenceHero && { ...referenceHero.stats(), position: referenceHero.root.position.toArray(), yaw: referenceHero.root.rotation.y },
        target: referenceTarget && { model: "corpo-seco-procedural", position: referenceTarget.root.position.toArray() },
        weapon: referenceWeaponId, weaponReady: Boolean(referenceWeapon), ribbon: referenceRibbon?.mesh.visible ?? false,
        ribbonVertices: referenceRibbon?.mesh.geometry.drawRange.count ?? 0,
        camera: { position: view.camera.position.toArray(), yaw: controller.state.yaw, pitch: controller.state.pitch, fov: view.camera.fov },
      },
      // F52: onde o peito de cada boneco cai na tela (0 a 1, de cima à esquerda), para o e2e ler a captura.
      avatars: avatars.map((doll) => {
        const ndc = doll.root.position.clone().setY(doll.root.position.y + 1).project(view.camera);
        return { ...doll.stats(), screen: [(ndc.x + 1) / 2, (1 - ndc.y) / 2] };
      }),
      fps: timer.fps,
      pool: world.pool.stats,
      pois: view.pois.instances,
      poiLayer: { drawn: view.pois.drawn, meshes: view.pois.meshes, focus: target && { id: target.id, look: poiLook(target, world.seed), colliders: focusColliders } },
      poiColliders: allColliders,
      structures: structures.size,
      blobs: blobs.stats(),
      details: { ...view.details.stats(), density: QUALITY.details, focus: detailTarget },
    }),
    blobTell: ({ grass = true } = {}) => {
      const light = view.atmosphere.uLightDir.value;
      const away = new Vector3(-light.x, 0, -light.z).normalize();
      // Para longe da luz (onde a sombra se alonga) e dos dois lados, de través: o corpo largo dos
      // avatares KayKit (F49) cobre o lado da sombra desta câmera no crepúsculo.
      const steps: [number, number][] = [[0.5, 0], [0.9, 0], [1.3, 0], [1.7, 0], [0.15, 0.6], [0.15, -0.6], [0.3, 0.85], [0.3, -0.85]];
      const points = people.flatMap((p) => steps.map(([along, side]) => {
        const x = p.x + (away.x * along - away.z * side) * PLAYER_BLOB_RADIUS;
        const z = p.z + (away.z * along + away.x * side) * PLAYER_BLOB_RADIUS;
        return new Vector3(x, height(x, z), z);
      }));
      const density = QUALITY.grass;
      const strength = view.atmosphere.uBlobStyle.value.x;
      const render = () => view.render();
      try {
        if (!grass) QUALITY.grass = 0;
        const on = probeAt(renderer, view.camera, render, points, view.atmosphere.uOutlineMask);
        view.atmosphere.uBlobStyle.value.x = 0;
        const off = probeAt(renderer, view.camera, render, points, view.atmosphere.uOutlineMask);
        return people.map((_, i) => ({ samples: steps.map((__, k) => ({ on: on[i * steps.length + k] ?? null, off: off[i * steps.length + k] ?? null })) }));
      } finally {
        QUALITY.grass = density;
        view.atmosphere.uBlobStyle.value.x = strength;
      }
    },
    grassTell: () => {
      const a = view.atmosphere;
      const cx = view.camera.position.x;
      const cz = view.camera.position.z;
      for (let r = 5; r <= 60; r += 5) {
        for (let k = 0; k < 8; k++) {
          const x = cx + Math.cos((k * Math.PI) / 4) * r;
          const z = cz + Math.sin((k * Math.PI) / 4) * r;
          if (view.grassShade(x, z, 1).covered < 20) continue;
          const saved = { count: a.uBlobCount.value, first: a.uBlobs.value[0]!.clone(), lift: a.uBlobLift.value[0]!, strength: a.uBlobStyle.value.x, fog: a.uFogDensity.value };
          try {
            // Sem neblina: a câmera da sonda fica 2 km acima, e a neblina cobriria quase toda a cor.
            a.uFogDensity.value = 0;
            // O capim em tufos (Fase 12, célula de 1,35 m): as cunhas no quadrado de ±1 m podem vir de um tufo
            // com a raiz (onde a sombra é lida) a até ~2 m; raio 2,5 m cobre todas, e o corte de altura é o
            // dos atores (o slot 0 podia ser de uma árvore, com o corte baixo dos recursos).
            a.uBlobs.value[0]!.set(x, z, 2.5, height(x, z));
            a.uBlobLift.value[0] = 0;
            a.uBlobCount.value = 1;
            const on = view.grassShade(x, z, 1);
            a.uBlobStyle.value.x = 0;
            const off = view.grassShade(x, z, 1);
            return { x, z, covered: on.covered, on: on.rgb, off: off.rgb };
          } finally {
            a.uBlobCount.value = saved.count;
            a.uBlobs.value[0]!.copy(saved.first);
            a.uBlobLift.value[0] = saved.lift;
            a.uBlobStyle.value.x = saved.strength;
            a.uFogDensity.value = saved.fog;
          }
        }
      }
      return undefined;
    },
    shading: (options) =>
      shadingProbe(
        renderer,
        view.camera,
        (p, fx) => {
          view.setPhase(p, 0);
          view.render(fx);
        },
        options,
        view.atmosphere,
      ),
    stop() {
      renderer.setAnimationLoop(null);
      // O acento do mundo é global (Fase 13): a próxima cena da página não herda a classe do quadro.
      if (reference) setWorldAccent(undefined);
      post.dispose();
      unwatch();
      structures.dispose();
      referenceRibbon?.dispose();
      referenceHero?.dispose();
      referenceTarget?.root.removeFromParent();
      for (const avatar of avatars) avatar.dispose();
      view.dispose();
      hud.remove();
      world.pool.terminate();
    },
  };
}

/**
 * Câmera olhando a primeira instância do recurso (no bioma pedido, se houver: `&bioma=cerrado`), a
 * uma distância que enquadra a altura do colisor (7 m no mínimo), um pouco acima do chão.
 */
function resourceFocus(world: LoadedWorld, id: string, biome?: string): { x: number; y: number; z: number; yaw: number; pitch: number } | undefined {
  const type = RESOURCE_TYPES.find((t) => t.id === id);
  const set = world.resources;
  const biomeAt = planBiomes(world.plan.biome);
  const wanted = biome === undefined ? -1 : (BIOME_NAMES as readonly string[]).indexOf(biome);
  const height = type?.collider?.[1] ?? 2;
  const distance = Math.max(7, 1.1 * height);
  for (let k = 0; type && k < set.count; k++) {
    if (set.types[k] !== type.index) continue;
    const tx = set.x[k] as number;
    const tz = set.z[k] as number;
    if (wanted >= 0 && biomeAt(tx, tz) !== wanted) continue;
    const x = tx + distance;
    const z = tz;
    const y = Math.max(sampleHeight(world.heightfield, x, z) + 1.5, (set.y[k] as number) + Math.max(2.5, 0.45 * height));
    return { x, y, z, yaw: Math.atan2(x - tx, z - tz), pitch: height > 4 ? -0.08 : -0.25 };
  }
  return undefined;
}

/**
 * Câmera olhando o primeiro enfeite do tipo (`&detalhe=muda`), no bioma pedido (`&bioma=mata`), de
 * perto e na altura do olho, um pouco de lado. Procura chunk a chunk no worker, a partir do centro da
 * ilha (as tábuas, a partir dos Destroços).
 */
async function detailFocus(world: LoadedWorld, id: string, biome?: string): Promise<{ pose: FreeCameraState; target: { x: number; z: number; biome: string } } | undefined> {
  const rule = GROUND_DETAIL_BY_ID.get(id as GroundDetailId);
  if (!rule) return undefined;
  const pieces = rule.pieces.map((p) => DETAIL_PIECE_IDS.indexOf(p));
  const wanted = biome === undefined ? -1 : (BIOME_NAMES as readonly string[]).indexOf(biome);
  const from = (rule.shore && world.plan.pois.find((p) => rule.shore!.near.includes(p.type))) || { x: 0, z: 0 };
  const order: [number, number][] = [];
  for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) order.push([cx, cz]);
  const center = ([cx, cz]: [number, number]) => Math.hypot(-WORLD_HALF + (cx + 0.5) * CHUNK_SIZE - from.x, -WORLD_HALF + (cz + 0.5) * CHUNK_SIZE - from.z);
  order.sort((a, b) => center(a) - center(b));
  for (const [cx, cz] of order) {
    const { chunk } = await world.pool.details(cx, cz);
    for (let g = 0; g < DETAIL_GROUPS; g++) {
      const piece = g % PIECE_COUNT;
      if (!pieces.includes(piece)) continue;
      for (let k = chunk.offsets[g]!; k < chunk.offsets[g + 1]!; k++) {
        if (wanted >= 0 && chunk.biome[k] !== wanted) continue;
        const m = chunk.matrices.subarray(k * 16, k * 16 + 16);
        const tx = m[12]!;
        const tz = m[14]!;
        const ty = m[13]!;
        const [w, h, d] = DETAIL_PIECES[DETAIL_PIECE_IDS[piece]!].size;
        const scale = Math.hypot(m[0]!, m[1]!, m[2]!);
        // Do lado, a uma distância que enquadra a peça (3 m no mínimo), na altura do olho.
        const distance = Math.max(3, 2.2 * Math.max(w, h, d) * scale);
        const x = tx + distance * 0.8;
        const z = tz + distance * 0.6;
        const y = Math.max(sampleHeight(world.heightfield, x, z), ty) + 1.6;
        const pitch = -Math.atan2(y - (ty + 0.4 * h * scale), distance);
        return { pose: { x, y, z, yaw: Math.atan2(x - tx, z - tz), pitch }, target: { x: tx, z: tz, biome: BIOME_NAMES[chunk.biome[k]!] ?? "?" } };
      }
    }
  }
  return undefined;
}
